import type { Game } from '../game/Game';
import type { NetHooks, ZoneOpts } from '../game/netHooks';
import type { ZoneRuntime } from '../game/ZoneRuntime';
import { itemLevel } from '../game/character';
import { addStack, countOf, freeSlots, removeStack } from '../game/inventory';
import { ITEMS } from '../game/items';
import { getZone, hasZone } from '../content';
import { registry } from '../sim/registry';
import type { PartyMemberView } from '../ui/Hud';
import { bump, ui } from '../ui/app/store';
import { type FriendRecord, FriendsManager } from './friends';
import { GuildManager } from './guild';
import { MatchMaker, type QueueSpec } from './matchmaking';
import { type ChatChannel, netUi, pushChat, systemChat } from './netStore';
import { type EnterInfo, type MemberInfo, PARTY_MAX, PartyManager } from './party';
import { Replicator } from './Replicator';
import { TradeManager } from './trade';
import { type JsonMessage, type Room, type Transport, asText, randomId } from './transport';
import { BroadcastTransport } from './transports/broadcast';
import { TrysteroTransport } from './transports/trystero';

export type NetMode = 'off' | 'public' | 'relay' | 'broadcast';

/** Zone kinds everyone in the world shares (one room per zone channel). */
const SHARED_KINDS = new Set(['field', 'town', 'sea', 'island']);
export const CHANNEL_CAP = 16;
const CHAT_WINDOW_MS = 5000;
const CHAT_MAX_IN_WINDOW = 6;
const REJOIN_MS = 20 * 60 * 1000;
const TRADABLE_KINDS = new Set(['material', 'consumable', 'battle', 'book', 'gem', 'food', 'junk']);
const TRADE_MESSAGES = new Set(['tinv', 'tacc', 'tno', 'tbusy', 'toffer', 'tlock', 'tok', 'tx']);
export const DEV_RELAY_URL = 'ws://127.0.0.1:8787';

/** Picks the transport: `?net=` overrides the "Online play" setting. */
export function pickMode(online: boolean, search: string): NetMode {
  const q = new URLSearchParams(search).get('net');
  if (q === 'off') return 'off';
  if (q === 'relay' || q === 'wsrelay') return 'relay';
  if (q === 'bc' || q === 'broadcast') return 'broadcast';
  if (q === 'public') return 'public';
  return online ? 'public' : 'off';
}

export function createTransport(mode: NetMode): Transport | null {
  switch (mode) {
    case 'off':
      return null;
    case 'broadcast':
      return new BroadcastTransport();
    case 'relay':
      return new TrysteroTransport({ mode: 'relay', relayUrl: DEV_RELAY_URL });
    case 'public':
      return new TrysteroTransport({ mode: 'public' });
  }
}

/** Strips control characters and trims chat text. */
export function cleanText(s: string, max = 200): string {
  let out = '';
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    if (c < 32 || (c >= 0x7f && c < 0xa0) || c === 0x202e || c === 0x202d) continue;
    out += ch;
  }
  return out.trim().slice(0, max);
}

interface ZoneLink {
  zone: ZoneRuntime;
  roomId: string;
  room: Room | null;
  rep: Replicator | null;
  offs: (() => void)[];
}

interface PendingInvite {
  kind: 'party' | 'guild' | 'trade' | 'friend';
  from: string;
  payload: JsonMessage;
}

interface RejoinInfo {
  zone: string;
  opts: ZoneOpts;
  at: number;
}

const CLASS_LETTER = (classId: string): string => (registry.classes.get(classId)?.name ?? classId).slice(0, 1).toUpperCase();

/**
 * The multiplayer session: owns the transport and every room this client is
 * in (zone channel or instance, party, guild, matchmaking queue), and exposes
 * the social features to the game and the UI.
 */
export class NetSession implements NetHooks {
  /** UI state (exposed for tests and debugging via `__AF__.net.ui`). */
  readonly ui = netUi;
  transport: Transport | null = null;
  mode: NetMode = 'off';
  readonly party: PartyManager;
  readonly queue: MatchMaker;
  readonly trade: TradeManager;
  readonly guild: GuildManager;
  readonly friends: FriendsManager;
  private initP: Promise<void> = Promise.resolve();
  private link: ZoneLink | null = null;
  private chatTimes = new Map<string, number[]>();
  private lastWhisper: { name: string; peer: string } | null = null;
  private invites = new Map<string, PendingInvite>();
  private refreshT = 0;
  /** Called to play an emote on any actor's view (installed by the emote feature). */
  emoteHandler: ((zone: ZoneRuntime, actorId: number, emote: string) => void) | null = null;

  constructor(private game: Game) {
    const now = () => Date.now();
    const transport = () => this.transport as Transport;
    this.party = new PartyManager({
      get transport() {
        return transport();
      },
      self: () => this.memberInfo(),
      now,
      changed: () => this.syncParty(),
      enter: (info) => this.enterGroup(info, null),
      notify: (t) => this.notify(t),
      chat: (name, text, peer) => this.receiveChat('party', name, text, peer),
      persist: (p) => this.persistParty(p),
      other: (from, m) => this.onDirectMessage(from, m),
    });
    this.queue = new MatchMaker({
      get transport() {
        return transport();
      },
      self: () => {
        const m = this.memberInfo();
        return { uid: m.uid, name: m.name, classId: m.classId, level: m.level, ilvl: m.ilvl };
      },
      now,
      changed: () => this.syncQueue(),
      enter: (info, team) => this.enterGroup(info, team),
      notify: (t) => this.notify(t),
    });
    this.guild = new GuildManager({
      get transport() {
        return transport();
      },
      self: () => {
        const m = this.memberInfo();
        return { uid: m.uid, name: m.name, classId: m.classId, level: m.level, zone: m.zone };
      },
      now,
      changed: () => this.syncGuild(),
      notify: (t) => this.notify(t),
      chat: (name, text, peer) => this.receiveChat('guild', name, text, peer),
      other: (from, m) => this.onDirectMessage(from, m),
      save: (g) => {
        this.game.account.guild = g;
        this.game.store.saveSoon(this.game.account);
        bump();
      },
    });
    this.friends = new FriendsManager({
      get transport() {
        return transport();
      },
      self: () => {
        const m = this.memberInfo();
        return { uid: m.uid, name: m.name, classId: m.classId, level: m.level, zone: m.zone };
      },
      now,
      changed: () => this.syncFriends(),
      notify: (t) => this.notify(t),
      load: () => this.game.progression?.char.friends ?? [],
      save: (list: FriendRecord[]) => {
        const c = this.game.progression?.char;
        if (!c) return;
        c.friends = list;
        this.game.store.saveSoon(this.game.account);
      },
      sendShared: (peer, msg) => this.sendTo(peer, msg),
      requested: (rid, name) => this.addInvite({ kind: 'friend', from: '', payload: { t: 'freq', rid } }, name, 'wants to be friends'),
      other: (from, m) => this.onDirectMessage(from, m),
    });
    this.trade = new TradeManager({
      selfId: () => this.transport?.selfId ?? '',
      selfName: () => this.game.progression?.char.name ?? '?',
      now,
      // Trading needs the partner in the same zone.
      send: (msg, to) => {
        if (!this.link?.room || !this.link.rep?.peerMap.has(to)) return false;
        this.link.room.send(msg, to);
        return true;
      },
      inventory: {
        count: (id) => (this.game.progression ? countOf(this.game.progression.char, id) : 0),
        tradable: (id) => !!ITEMS[id] && TRADABLE_KINDS.has((ITEMS[id] as { kind: string }).kind),
        take: (id, n) => !!this.game.progression && removeStack(this.game.progression.char, id, n),
        give: (id, n) => (this.game.progression ? addStack(this.game.progression.char, id, n) : 0),
        silver: () => this.game.account.wallet.silver,
        takeSilver: (n) => {
          const w = this.game.account.wallet;
          if (w.silver < n) return false;
          w.silver -= n;
          return true;
        },
        giveSilver: (n) => {
          this.game.account.wallet.silver += n;
        },
        freeSlots: () => (this.game.progression ? freeSlots(this.game.progression.char) : 0),
      },
      changed: () => {
        this.syncTrade();
        bump();
        this.game.store.saveSoon(this.game.account);
      },
      notify: (t) => this.notify(t),
      invited: (tid, from, name) => this.addInvite({ kind: 'trade', from, payload: { t: 'tinv', tid, name } }, name, 'wants to trade'),
    });
  }

  // ================================================================= lifecycle

  /** Creates the transport for the chosen mode; the game waits on `ready()` before entering a zone. */
  start(mode: NetMode): void {
    this.mode = mode;
    netUi.mode.value = mode;
    const t = createTransport(mode);
    if (!t) {
      netUi.status.value = 'offline';
      return;
    }
    this.transport = t;
    netUi.status.value = 'connecting';
    this.initP = t
      .init()
      .then(() => {
        this.game.localPeer = t.selfId;
        netUi.selfId.value = t.selfId;
        netUi.status.value = 'online';
      })
      .catch((e: unknown) => {
        console.warn('network unavailable', e);
        this.transport = null;
        this.mode = 'off';
        netUi.status.value = 'offline';
        netUi.warning.value = 'Could not start online play; playing offline.';
      });
    this.game.zoneSetups.push((_g, z, opts) => this.attachZone(z, opts));
    this.game.systems.push(() => this.tick());
  }

  ready(): Promise<void> {
    return Promise.race([this.initP, new Promise<void>((r) => setTimeout(r, 6000))]);
  }

  get online(): boolean {
    return this.transport !== null && netUi.status.value === 'online';
  }

  get peerCount(): number {
    return this.link?.rep?.peerMap.size ?? 0;
  }

  get replicator(): Replicator | null {
    return this.link?.rep ?? null;
  }

  onCharacter(): void {
    const prog = this.game.progression;
    if (!prog || !this.online) return;
    const party = this.loadSession<{ id: string; key: string }>('party');
    if (party?.id && party.key) void this.party.join(party.id, party.key);
    const g = this.game.account.guild;
    if (g && typeof g.id === 'string' && typeof g.key === 'string') void this.guild.open(g);
    this.friends.open();
    const rejoin = this.loadSession<RejoinInfo>('rejoin');
    if (rejoin && Date.now() - rejoin.at < REJOIN_MS && hasZone(rejoin.zone)) {
      ui.confirm.value = {
        text: `Your group is still in ${rejoin.zone}. Rejoin them?`,
        okLabel: 'Rejoin',
        ok: () => this.game.enterInstance(rejoin.zone, rejoin.opts),
      };
    }
  }

  onLogout(): void {
    this.queue.cancel();
    this.trade.cancel();
    this.party.leave();
    this.guild.close();
    netUi.guild.value = null;
    this.friends.close();
    netUi.friends.value = [];
  }

  leftInstance(): void {
    this.saveSession('rejoin', null);
  }

  // ================================================================= zones

  private attachZone(z: ZoneRuntime, opts: ZoneOpts): void {
    this.detachZone();
    if (!this.online || !this.transport) return;
    const kind = z.layout.kind;
    let roomId: string | null = null;
    let password: string | undefined;
    let hostHint: string | null = null;
    let rate = 15;
    if (typeof opts.inst === 'string' && opts.inst) {
      // Keep the rejoin record pointing at the current gate / floor.
      this.saveSession('rejoin', { zone: z.layout.id, opts, at: Date.now() } satisfies RejoinInfo);
      roomId = `i:${opts.inst}`;
      password = typeof opts.pw === 'string' ? opts.pw : undefined;
      hostHint = typeof opts.host === 'string' ? opts.host : null;
      rate = 20;
    } else if (SHARED_KINDS.has(kind)) {
      roomId = `z:${z.layout.id}:${netUi.channel.value}`;
    }
    netUi.zonePeers.value = [];
    netUi.isHost.value = true;
    if (!roomId) return;
    const link: ZoneLink = { zone: z, roomId, room: null, rep: null, offs: [] };
    this.link = link;
    z.scripts.push({ dispose: () => this.detachZone(link) });
    void this.transport
      .join(roomId, password ? { password } : {})
      .then((room) => {
        if (this.link !== link) {
          void room.leave();
          return;
        }
        link.room = room;
        const rep = new Replicator({ room, zone: z, joinedAt: Date.now(), hostHint, rate, profileExtra: () => this.profileExtra() });
        link.rep = rep;
        link.offs.push(
          rep.on((e) => {
            if (e.type === 'version') netUi.warning.value = 'A nearby player runs a different game version.';
            else if (e.type === 'host') netUi.isHost.value = e.self;
            else if (e.type === 'peerLeave') this.trade.peerGone(e.peer.id);
            this.refreshZonePeers();
          }),
          room.onMessage((from, m) => this.onZoneMessage(from, m)),
        );
      })
      .catch((e: unknown) => {
        console.warn('zone room failed', e);
        netUi.warning.value = 'Could not reach other players in this zone.';
      });
  }

  private detachZone(link: ZoneLink | null = this.link): void {
    if (!link) return;
    if (this.link === link) this.link = null;
    for (const off of link.offs) off();
    link.offs = [];
    link.rep?.dispose();
    void link.room?.leave();
    link.room = null;
    link.rep = null;
    netUi.zonePeers.value = [];
  }

  /** Moves to another channel of the current field/town (reloads the zone). */
  switchChannel(n: number): void {
    const z = this.game.zone;
    if (!z || !SHARED_KINDS.has(z.layout.kind) || n < 1 || n > 9) return;
    netUi.channel.value = n;
    this.game.reloadZone();
  }

  private profileExtra(): { ilvl: number; party: string | null; title: string | null; guild: string | null } {
    const prog = this.game.progression;
    return { ilvl: prog ? itemLevel(prog.char) : 0, party: this.party.id, title: null, guild: this.guild.data?.tag ?? null };
  }

  private refreshZonePeers(): void {
    const rep = this.link?.rep;
    netUi.zonePeers.value = rep
      ? rep
          .peerList()
          .filter((p) => p.profile)
          .map((p) => ({ peer: p.id, name: p.profile?.name ?? '?', classId: p.profile?.classId ?? '', level: p.profile?.level ?? 1, ilvl: p.profile?.ilvl ?? 0, party: p.profile?.party ?? null, guild: p.profile?.guild ?? null }))
      : [];
  }

  // ================================================================= group content

  interceptInstance(zone: string, opts: ZoneOpts): boolean {
    if (typeof opts.inst === 'string') return false;
    const p = this.party;
    if (!p.inParty || p.onlineMembers().length < 2) return false;
    if (!p.isLeader) {
      this.notify('Your party leader starts group content.');
      return true;
    }
    if (p.ready) {
      this.notify('A ready check is already running.');
      return true;
    }
    const label = hasZone(zone) ? getZone(zone).name : zone;
    return p.startReadyCheck(zone, label, opts);
  }

  private enterGroup(info: EnterInfo, team: 'red' | 'blue' | null): void {
    const opts: ZoneOpts = { ...info.opts, inst: info.inst, pw: info.pw, host: info.host, size: info.size };
    if (team) opts.team = team;
    this.saveSession('rejoin', { zone: info.zone, opts, at: Date.now() } satisfies RejoinInfo);
    this.game.enterInstance(info.zone, opts);
  }

  /** Queues for matchmade content. */
  joinQueue(spec: QueueSpec): void {
    if (!this.online) {
      this.notify('Matchmaking needs online play.');
      return;
    }
    if (this.party.inParty && this.party.onlineMembers().length > 1) {
      this.notify('Parties enter together from the instance list.');
      return;
    }
    void this.queue.join(spec);
  }

  // ================================================================= messaging

  /** Sends to a peer through any room we share with it. */
  sendTo(peer: string, msg: JsonMessage): boolean {
    const rep = this.link?.rep;
    if (rep?.peerMap.has(peer) && this.link?.room) {
      this.link.room.send(msg, peer);
      return true;
    }
    if (this.party.memberByPeer(peer) && this.party.room) {
      this.party.room.send(msg, peer);
      return true;
    }
    for (const e of this.guild.roster.values()) {
      if (e.peer === peer && this.guild.room) {
        this.guild.room.send(msg, peer);
        return true;
      }
    }
    return this.friends.sendTo(peer, msg);
  }

  /** Finds a reachable player by (case-insensitive) name. */
  findPeer(name: string): { peer: string; name: string } | null {
    const n = name.trim().toLowerCase();
    for (const p of this.link?.rep?.peerList() ?? []) if (p.profile?.name.toLowerCase() === n) return { peer: p.id, name: p.profile.name };
    for (const m of this.party.members.values()) if (m.peer && m.name.toLowerCase() === n && m.peer !== this.transport?.selfId) return { peer: m.peer, name: m.name };
    for (const e of this.guild.roster.values()) if (e.peer && e.name.toLowerCase() === n && e.peer !== this.transport?.selfId) return { peer: e.peer, name: e.name };
    return this.friends.findByName(name);
  }

  private onZoneMessage(from: string, m: JsonMessage): void {
    switch (m.t) {
      case 'chat': {
        const text = typeof m.text === 'string' ? m.text : '';
        const name = this.link?.rep?.peerMap.get(from)?.profile?.name ?? asText(m.name, '?', 24);
        if (m.ch === 'whisper') this.receiveChat('whisper', name, text, from);
        else this.receiveChat('zone', name, text, from);
        return;
      }
      case 'emote': {
        const rep = this.link?.rep;
        const actorId = rep?.peerMap.get(from)?.actorId;
        if (this.link && actorId != null && typeof m.e === 'string') this.emoteHandler?.(this.link.zone, actorId, m.e);
        return;
      }
      case 'pinv': {
        const name = this.link?.rep?.peerMap.get(from)?.profile?.name ?? asText(m.leader, '?', 24);
        this.addInvite({ kind: 'party', from, payload: m }, name, `invites you to a party (${Number(m.size) || 1}/${PARTY_MAX})`);
        return;
      }
      case 'ginv': {
        const name = this.link?.rep?.peerMap.get(from)?.profile?.name ?? '?';
        this.addInvite({ kind: 'guild', from, payload: m }, name, `invites you to <${asText(m.tag)}> ${asText(m.name)}`);
        return;
      }
      case 'freq':
      case 'facc':
      case 'fno':
        this.friends.onShared(from, m);
        return;
      case 'pinv_no': {
        const name = this.link?.rep?.peerMap.get(from)?.profile?.name ?? 'The player';
        this.notify(`${name} declined your party invite.`);
        return;
      }
      default:
        if (TRADE_MESSAGES.has(m.t)) this.trade.onMessage(from, m);
    }
  }

  /** Whispers that travel through the party or guild room (the recipient is elsewhere). */
  private onDirectMessage(from: string, m: JsonMessage): void {
    if (this.friends.onShared(from, m)) return;
    if (m.t === 'pinv') {
      const f = this.friends.list().find((x) => x.presence.peer === from);
      this.addInvite({ kind: 'party', from, payload: m }, f?.friend.name ?? asText(m.leader, '?', 24), `invites you to a party (${Number(m.size) || 1}/${PARTY_MAX})`);
      return;
    }
    if (m.t !== 'chat' || m.ch !== 'whisper' || typeof m.text !== 'string') return;
    this.receiveChat('whisper', asText(m.name, '?', 24), m.text, from);
  }

  private allowChat(from: string): boolean {
    const now = Date.now();
    const list = (this.chatTimes.get(from) ?? []).filter((t) => now - t < CHAT_WINDOW_MS);
    if (list.length >= CHAT_MAX_IN_WINDOW) return false;
    list.push(now);
    this.chatTimes.set(from, list);
    return true;
  }

  private receiveChat(ch: ChatChannel, name: string, raw: string, peer: string): void {
    const text = cleanText(raw);
    if (!text || !this.allowChat(peer)) return;
    if (ch === 'whisper') this.lastWhisper = { name, peer };
    pushChat({ ch, from: name, text, self: false, peer });
  }

  chat(raw: string, channel: string): void {
    const text = cleanText(raw);
    if (!text) return;
    if (text.startsWith('/')) {
      this.command(text);
      return;
    }
    const me = this.game.progression?.char.name ?? 'You';
    switch (channel) {
      case 'party':
        if (!this.party.inParty) return systemChat('You are not in a party.');
        this.party.chat(text);
        pushChat({ ch: 'party', from: me, text, self: true });
        return;
      case 'guild':
        if (!this.guild.data) return systemChat('You are not in a guild.');
        this.guild.chat(text);
        pushChat({ ch: 'guild', from: me, text, self: true });
        return;
      default:
        if (!this.link?.room) return systemChat(this.online ? 'Nobody else is here.' : 'You are playing offline.');
        this.link.room.send({ t: 'chat', ch: 'zone', name: me, text });
        pushChat({ ch: 'zone', from: me, text, self: true });
    }
  }

  whisper(name: string, text: string): void {
    const target = this.findPeer(name);
    if (!target) return systemChat(`${name} is not reachable.`);
    const me = this.game.progression?.char.name ?? 'You';
    const clean = cleanText(text);
    if (!clean) return;
    this.sendTo(target.peer, { t: 'chat', ch: 'whisper', name: me, text: clean });
    pushChat({ ch: 'whisper', from: `To ${target.name}`, text: clean, self: true, peer: target.peer });
  }

  /** Slash commands typed into chat. */
  private command(line: string): void {
    const [cmd = '', ...rest] = line.slice(1).split(' ');
    const arg = rest.join(' ');
    switch (cmd.toLowerCase()) {
      case 'w':
      case 'whisper':
      case 't':
      case 'tell': {
        const [name = '', ...msg] = arg.split(' ');
        return this.whisper(name, msg.join(' '));
      }
      case 'r':
      case 'reply':
        if (!this.lastWhisper) return systemChat('Nobody to reply to.');
        return this.whisper(this.lastWhisper.name, arg);
      case 'p':
      case 'party':
        return this.chat(arg, 'party');
      case 'g':
      case 'guild':
        return this.chat(arg, 'guild');
      case 's':
      case 'z':
      case 'say':
        return this.chat(arg, 'zone');
      case 'invite':
      case 'inv': {
        const t = this.findPeer(arg);
        return t ? void this.invite(t.peer) : systemChat(`${arg} is not nearby.`);
      }
      case 'leave':
        return this.party.leave();
      case 'trade': {
        const t = this.findPeer(arg);
        return t ? void this.trade.request(t.peer, t.name) : systemChat(`${arg} is not nearby.`);
      }
      case 'friend':
      case 'addfriend': {
        const t = this.findPeer(arg);
        return t ? void this.friends.request(t.peer, t.name) : systemChat(`${arg} is not nearby.`);
      }
      case 'ginvite': {
        const t = this.findPeer(arg);
        return t ? void this.inviteToGuild(t.peer) : systemChat(`${arg} is not nearby.`);
      }
      case 'ch':
      case 'channel':
        return this.switchChannel(Number(arg));
      case 'e':
      case 'emote':
        return this.game.emote(arg);
      default:
        systemChat('Commands: /w name text, /r, /p, /g, /s, /invite name, /leave, /trade name, /ginvite name, /channel n, /e emote');
    }
  }

  emote(id: string): void {
    this.link?.room?.send({ t: 'emote', e: id });
  }

  // ================================================================= invites

  private addInvite(inv: PendingInvite, fromName: string, detail: string): void {
    const id = randomId(8);
    this.invites.set(id, inv);
    netUi.invites.value = [...netUi.invites.value, { id, kind: inv.kind, from: inv.from, fromName, detail, expires: Date.now() + 30000 }];
  }

  answerInvite(id: string, ok: boolean): void {
    const inv = this.invites.get(id);
    this.invites.delete(id);
    netUi.invites.value = netUi.invites.value.filter((i) => i.id !== id);
    if (!inv) return;
    const m = inv.payload;
    if (inv.kind === 'party') {
      if (ok && typeof m.pid === 'string' && typeof m.key === 'string') void this.party.join(m.pid, m.key);
      else this.sendTo(inv.from, { t: 'pinv_no' });
    } else if (inv.kind === 'guild') {
      if (ok && typeof m.gid === 'string' && typeof m.key === 'string') void this.guild.accept({ gid: m.gid, key: m.key, name: asText(m.name), tag: asText(m.tag) });
    } else if (inv.kind === 'friend' && typeof m.rid === 'string') {
      this.friends.answer(m.rid, ok);
    } else if (inv.kind === 'trade' && typeof m.tid === 'string') {
      if (ok) this.trade.accept(m.tid);
      else this.trade.decline(m.tid);
    }
  }

  /** Invites a nearby player to our party (creating one if needed). */
  async invite(peer: string): Promise<void> {
    if (!this.online) return;
    if (this.party.inParty && !this.party.isLeader) return this.notify('Only the party leader can invite.');
    if (this.party.order.length >= PARTY_MAX) return this.notify('Your party is full.');
    if (!this.party.inParty) await this.party.create();
    const msg = this.party.inviteMessage();
    const name = this.link?.rep?.peerMap.get(peer)?.profile?.name ?? 'them';
    if (msg && this.sendTo(peer, msg)) this.notify(`Party invite sent to ${name}.`);
  }

  inviteToGuild(peer: string): void {
    const msg = this.guild.inviteMessage();
    if (!msg) return this.notify('You are not in a guild.');
    if (this.sendTo(peer, msg)) this.notify('Guild invite sent.');
  }

  // ================================================================= views

  private memberInfo(): MemberInfo {
    const prog = this.game.progression;
    const p = this.game.player;
    return {
      uid: prog?.char.id ?? 'none',
      name: prog?.char.name ?? '?',
      classId: prog?.char.classId ?? '',
      level: prog?.char.level ?? 1,
      ilvl: prog ? itemLevel(prog.char) : 0,
      zone: this.game.zoneId,
      hpPct: p ? p.hpPct : 1,
      alive: p ? p.alive : true,
    };
  }

  partyFrames(): PartyMemberView[] {
    const p = this.party;
    if (!p.inParty) return [];
    const self = p.selfUid;
    const world = this.game.zone?.world;
    const rep = this.link?.rep;
    const out: PartyMemberView[] = [];
    for (const uid of p.order) {
      if (uid === self) continue;
      const m = p.members.get(uid);
      if (!m) continue;
      let hpPct = m.hpPct;
      let dead = !m.alive;
      const actorId = m.peer ? rep?.peerMap.get(m.peer)?.actorId : null;
      const a = actorId != null ? world?.actors.get(actorId) : undefined;
      if (a) {
        hpPct = a.hpPct;
        dead = !a.alive;
      }
      out.push({ name: m.peer ? m.name : `${m.name} (offline)`, classLetter: CLASS_LETTER(m.classId), hpPct, leader: uid === p.leader, dead, level: m.level });
    }
    return out;
  }

  channelLabel(): string | null {
    const z = this.game.zone;
    if (!z) return null;
    if (SHARED_KINDS.has(z.layout.kind)) return this.online ? `Channel ${netUi.channel.value}` : 'Offline';
    return this.link ? 'Group Instance' : 'Instance';
  }

  private syncParty(): void {
    const p = this.party;
    const self = p.inParty ? p.selfUid : '';
    netUi.party.value = p.inParty
      ? p.order.map((uid) => {
          const m = p.members.get(uid);
          return {
            uid,
            peer: m?.peer ?? null,
            name: m?.name ?? '?',
            classId: m?.classId ?? '',
            level: m?.level ?? 1,
            ilvl: m?.ilvl ?? 0,
            zone: m?.zone ?? null,
            hpPct: m?.hpPct ?? 1,
            alive: m?.alive ?? true,
            leader: uid === p.leader,
            self: uid === self,
          };
        })
      : [];
    const r = p.ready;
    netUi.readyCheck.value = r
      ? {
          rid: r.rid,
          label: r.label,
          leaderName: p.members.get(r.leaderUid)?.name ?? 'Leader',
          expires: r.expires,
          mine: r.mine,
          isLeader: p.isLeader,
          responses: [...r.responses].map(([uid, ok]) => ({ name: p.members.get(uid)?.name ?? '?', ok })),
        }
      : null;
  }

  private syncQueue(): void {
    const q = this.queue;
    netUi.queue.value = q.spec ? { content: q.spec.content, label: q.spec.label, since: q.since, count: q.count(), size: q.spec.size } : null;
  }

  private syncGuild(): void {
    const g = this.guild.data;
    netUi.guild.value = g
      ? {
          name: g.name,
          tag: g.tag,
          rank: g.rank,
          members: [...this.guild.roster.values()].map((e) => ({ uid: e.uid, name: e.name, classId: e.classId, level: e.level, zone: e.zone, online: e.peer !== null, rank: e.rank })),
        }
      : null;
  }

  private syncFriends(): void {
    netUi.friends.value = this.friends
      .list()
      .map(({ friend, presence }) => ({ uid: friend.uid, name: friend.name, classId: friend.classId, online: presence.peer !== null, zone: presence.zone, level: presence.level }))
      .sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
  }

  private syncTrade(): void {
    const s = this.trade.session;
    netUi.trade.value = s
      ? {
          tid: s.tid,
          peer: s.peer,
          peerName: s.peerName,
          mine: { items: s.mine.items, silver: s.mine.silver, locked: s.myLock, confirmed: s.myOk },
          theirs: { items: s.theirs.items, silver: s.theirs.silver, locked: s.theirLock, confirmed: s.theirOk },
        }
      : null;
  }

  // ================================================================= upkeep

  private tick(): void {
    this.party.tick();
    this.queue.tick();
    this.guild.tick();
    this.trade.tick();
    this.friends.tick();
    const now = Date.now();
    if (now - this.refreshT > 500) {
      this.refreshT = now;
      const rep = this.link?.rep;
      if (rep) {
        const rtts = rep.peerList().map((p) => p.rtt).filter((r) => r > 0);
        netUi.rtt.value = rtts.length ? Math.round(rtts.reduce((a, b) => a + b, 0) / rtts.length) : 0;
        netUi.isHost.value = rep.isHost;
      }
      if (netUi.invites.value.some((i) => i.expires < now)) {
        for (const i of netUi.invites.value) if (i.expires < now) this.answerInvite(i.id, false);
      }
      this.syncQueue();
    }
  }

  private notify(text: string): void {
    this.game.notify(text);
    systemChat(text);
  }

  // ================================================================= persistence (per tab, per character)

  private sessionKey(kind: string): string | null {
    const id = this.game.progression?.char.id;
    return id ? `af:${kind}:${id}` : null;
  }

  private saveSession(kind: string, value: unknown): void {
    const k = this.sessionKey(kind);
    if (!k) return;
    try {
      if (value === null) sessionStorage.removeItem(k);
      else sessionStorage.setItem(k, JSON.stringify(value));
    } catch {
      /* storage unavailable (private mode): reconnection just won't be offered */
    }
  }

  private loadSession<T>(kind: string): T | null {
    const k = this.sessionKey(kind);
    if (!k) return null;
    try {
      const raw = sessionStorage.getItem(k);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  private persistParty(p: { id: string; key: string } | null): void {
    this.saveSession('party', p);
  }
}
